/**
 * @module constants/catalog
 * @description Catalog-related constants for Sephora e-commerce functionality.
 * Includes page types, refinement states and types, search sort options, and color mappings.
 * This module has no dependencies on other modules.
 */

/**
 * @constant {Object} PAGE_TYPES - Catalog page type identifiers
 * @property {string} NTH_CATEGORY_PAGE - Nth level category page
 * @property {string} CATEGORY_PAGE - Standard category page
 * @property {string} TOP_CATEGORY - Top-level category page
 */
const PAGE_TYPES = {
    NTH_CATEGORY_PAGE: 'NthCategory',
    CATEGORY_PAGE: 'Category',
    TOP_CATEGORY: 'TopCategory'
};

/**
 * @constant {Object} REFINEMENT_STATES - Product filter refinement states
 * @property {number} INACTIVE - Filter is not applied (value: 1)
 * @property {number} CHECKED - Filter is actively selected (value: 2)
 * @property {number} IMPLICIT - Filter is implicitly applied (value: 4)
 */
const REFINEMENT_STATES = {
    INACTIVE: 1,
    CHECKED: 2,
    IMPLICIT: 4
};

/**
 * @constant {Object} REFINEMENT_TYPES - Types of product filter refinements
 * @property {string} COLORS - Color filter type
 * @property {string} RADIOS - Radio button filter type
 * @property {string} RADIOS_CUSTOM - Custom radio button filter type
 * @property {string} CHECKBOXES - Checkbox filter type
 * @property {string} RANGE - Range/slider filter type
 * @property {string} SORT - Sort filter type
 * @property {string} CHECKBOXES_WITH_DROPDOWN - Checkbox with dropdown filter type
 * @property {string} CHECKBOXES_CUSTOM - Custom checkbox filter type
 * @property {string} BEAUTY_PREFERENCES - Beauty preferences filter type
 */
const REFINEMENT_TYPES = {
    COLORS: 'colors',
    RADIOS: 'radios',
    RADIOS_CUSTOM: 'radios_custom',
    CHECKBOXES: 'checkboxes',
    RANGE: 'range',
    SORT: 'sort',
    CHECKBOXES_WITH_DROPDOWN: 'checkboxesWithDropDown',
    CHECKBOXES_CUSTOM: 'checkboxes_custom',
    BEAUTY_PREFERENCES: 'beautyPreferences'
};

/**
 * @constant {Object} REFINEMENT_NAMES - Localized display names for specific refinements
 * @property {string} PICKUP_AND_DELIVERY - English name for pickup and delivery filter
 * @property {string} PICKUP_AND_DELIVERY_FR - French name for pickup and delivery filter
 */
const REFINEMENT_NAMES = {
    PICKUP_AND_DELIVERY: 'Pickup & Delivery',
    PICKUP_AND_DELIVERY_FR: 'Ramassage et livraison'
};

/**
 * @constant {Object} PRICE_KEYS - Query parameter keys for price filtering
 * @property {string} PL - Price low (minimum) key
 * @property {string} PH - Price high (maximum) key
 * @property {string} PTYPE - Price type key
 */
const PRICE_KEYS = {
    PL: 'pl',
    PH: 'ph',
    PTYPE: 'ptype'
};

/**
 * @constant {Object} PRICE_VALUES - Valid values for price filtering
 * @property {string} MANUAL - Manual price entry mode
 * @property {string} MIN - Minimum price value
 * @property {string} MAX - Maximum price value
 */
const PRICE_VALUES = {
    MANUAL: 'manual',
    MIN: 'min',
    MAX: 'max'
};

/**
 * @constant {string} MANUAL_PTYPE - Query string for manual price type
 * @description Formatted as "ptype=manual"
 */
const MANUAL_PTYPE = `${PRICE_KEYS.PTYPE}=${PRICE_VALUES.MANUAL}`;

/**
 * @constant {string[]} SINGLE_SELECTS - Array of refinement types that allow only single selection
 * @description Includes radio buttons, sort options, and range filters
 */
const SINGLE_SELECTS = [REFINEMENT_TYPES.RADIOS, REFINEMENT_TYPES.SORT, REFINEMENT_TYPES.RANGE];

/**
 * @constant {Object} ENDECA_VS_CONSTRUCTOR_COLORS - Mapping between color names and Constructor.io color IDs
 * @description Maps color names (English and French) to their corresponding Constructor.io identifier.
 * Includes both Endeca legacy colors and Constructor.io color system mappings.
 */
const ENDECA_VS_CONSTRUCTOR_COLORS = {
    berry: '9010109',
    red: '100007',
    pink: '100006',
    coral: '9010111',
    brown: '100002',
    nude: '100016',
    purple: '100011',
    black: '100010',
    clear: '100012',
    incolore: '100012',
    white: '100009',
    yellow: '100003',
    green: '100015',
    blue: '100013',
    orange: '100005',
    grey: '100001',
    gold: '100014',
    silver: '100008',
    universal: '9010110',
    multi: '100004',
    unconventional: '9010128',
    blanc: '100009',
    bleu: '100013',
    'eau de parfum gold': '100014',
    multiple: '100004',
    noir: '100010',
    'non conformiste': '9010128',
    rose: '100006',
    rouge: '100007',
    universel: '9010110',
    vert: '100015',
    violet: '100011',
    argent: '100008',
    brun: '100002',
    gris: '100001'
};

/**
 * @constant {string[]} BRAND_LETTERS - Alphabetical letters for brand filtering/navigation
 * @description Includes A-Z plus '#' for numeric brand names
 */
const BRAND_LETTERS = [
    'A',
    'B',
    'C',
    'D',
    'E',
    'F',
    'G',
    'H',
    'I',
    'J',
    'K',
    'L',
    'M',
    'N',
    'O',
    'P',
    'Q',
    'R',
    'S',
    'T',
    'U',
    'V',
    'W',
    'X',
    'Y',
    'Z',
    '#'
];

/**
 * @constant {Array<Object>} SEARCH_SORT_OPTIONS - Available product search sorting options
 * @description Each option contains display name, code identifier, and API value for sorting
 * @property {string} name - Display name for the sort option
 * @property {string} code - Code identifier for the sort option
 * @property {string} apiValue - API parameter value for applying the sort
 */
const SEARCH_SORT_OPTIONS = [
    {
        name: 'relevancy',
        code: 'RELEVANCY',
        apiValue: '-1'
    },
    {
        name: 'bestselling',
        code: 'BEST_SELLING',
        apiValue: 'P_BEST_SELLING:1::P_RATING:1::P_PROD_NAME:0'
    },
    {
        name: 'topRated',
        code: 'TOP_RATED',
        apiValue: 'P_RATING:1'
    },
    {
        name: 'exclusive',
        code: 'EXCLUSIVE',
        apiValue: 'P_SEPH_EXCLUSIVE:1'
    },
    {
        name: 'new',
        code: 'NEW',
        apiValue: 'P_NEW:1::P_START_DATE:1'
    },
    {
        name: 'priceDesc',
        code: 'PRICE_HIGH_TO_LOW',
        apiValue: 'price:1'
    },
    {
        name: 'priceAsc',
        code: 'PRICE_LOW_TO_HIGH',
        apiValue: 'price:0'
    },
    {
        name: 'brandName',
        code: 'BRAND_NAME',
        apiValue: 'P_BRAND_NAME:0'
    }
];

/**
 * @constant {RegExp} EXTRACT_KEY_FROM_FILTER_REGEX - Regular expression to extract key from filter parameter
 * @description Matches patterns like "filters[key]" and captures the key in a named group
 */
const EXTRACT_KEY_FROM_FILTER_REGEX = /filters\[(?<key>.*)\]/;

/**
 * @constant {number} BEAUTY_PREFERENCES_FILTER_LIMIT - Maximum number of beauty preferences filters
 * @description Limits the number of beauty preference filters that can be applied simultaneously
 */
const BEAUTY_PREFERENCES_FILTER_LIMIT = 25;

/**
 * @constant {number} PAGE_ONE - First page constant for pagination
 * @description Used as the default/initial page number in catalog pagination
 */
const PAGE_ONE = 1;

export {
    MANUAL_PTYPE,
    PRICE_KEYS,
    PRICE_VALUES,
    PAGE_TYPES,
    REFINEMENT_STATES,
    REFINEMENT_TYPES,
    REFINEMENT_NAMES,
    ENDECA_VS_CONSTRUCTOR_COLORS,
    SINGLE_SELECTS,
    BRAND_LETTERS,
    SEARCH_SORT_OPTIONS,
    PAGE_ONE,
    EXTRACT_KEY_FROM_FILTER_REGEX,
    BEAUTY_PREFERENCES_FILTER_LIMIT
};
